/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  formatProductPlanningFiles,
  getFileTypeFromOutputFileS3Key,
  hasKeys,
} from 'utils/appUtils';
import {
  GENERATE_SECTION_DESCRIPTION,
  OUTPUT_FILES,
  PUBLISH_STATUS,
} from 'utils/const';

const initialState = {
  selectedModelYear: new Date().getFullYear(),
  isSourceFilesSectionCollapsed: false,
  generateFilesSectionDescription: GENERATE_SECTION_DESCRIPTION,
  // SOURCE FILES START
  pplFilesByModelYearAndModelCode: {},
  pplFilesByModelYear: {},
  priceMatrixFilesByModelYear: {},
  nomenclatureGuideFilesByModelYear: {},
  techSpecsFilesByModelYear: {},
  // SOURCE FILES END
  haveAllSourceFiles: false,
  shouldOpenGetEffectiveDatesModal: false,
  shouldOpenConfirmPublishModal: false,
  userFileAction: '',
  selectedFilesToGenerateOrUpdate: {},
  selectedFilesForPublishing: {},
  // GENERATED FILES START
  mvdfFilesToBeGeneratedByModelYear: {},
  mvdfFilesByModelYearAndModelCode: {},
  mvdfsByModelYear: {},
  mvdfFileGenerationErrorsByModelYear: {},

  updatedPPLFilesByModelYear: [], // array of updated PPL files, used to generate MVDF files

  priceGuidesToBeGeneratedByModelYear: {},
  priceGuidesByModelYearAndModelCode: {},
  priceGuidesByModelYear: {},
  priceGuideGenerationErrorsByModelYear: {},

  vehicleTechSpecsToBeGeneratedByModelYear: {},
  vehicleTechSpecsByModelYearAndModelCode: {},
  vehicleTechSpecsByModelYear: {},
  vehicleTechSpecsGenerationErrorsByModelYear: {},

  topFeaturesToBeGeneratedByModelYear: {},
  topFeaturesByModelYearAndModelCode: {},
  topFeaturesByModelYear: {},
  topFeaturesGenerationErrorsByModelYear: {},

  featuresGuidesToBeGeneratedByModelYear: {},
  featuresGuidesByModelYearAndModelCode: {},
  featuresGuidesByModelYear: {},
  featuresGuideGenerationErrorsByModelYear: {},

  // GENERATED FILES END
  // PUBLISH FILES START
  publishOverviewFiles: [],
  // PUBLISH FILES END
};

const getGenerateAndUpdateState = ({
  pplFilesByModelYearAndModelCode,
  priceMatrixFilesByModelYear,
  nomenclatureGuideFilesByModelYear,
  techSpecsFilesByModelYear,
  mvdfFilesByModelYearAndModelCode,
  priceGuidesByModelYearAndModelCode,
  vehicleTechSpecsByModelYearAndModelCode,
  topFeaturesByModelYearAndModelCode,
  featuresGuidesByModelYearAndModelCode,
}) => {
  const updatedPPLFilesByModelYear = {};
  let updatedMvdfFilesByModelYearAndModelCode = {
    ...mvdfFilesByModelYearAndModelCode,
  };
  let updatedPriceGuidesByModelYearAndModelCode = {
    ...priceGuidesByModelYearAndModelCode,
  };
  let updatedVehicleTechSpecsByModelYearAndModelCode = {
    ...vehicleTechSpecsByModelYearAndModelCode,
  };
  let updatedTopFeaturesByModelYearAndModelCode = {
    ...topFeaturesByModelYearAndModelCode,
  };
  let updatedFeaturesGuidesByModelYearAndModelCode = {
    ...featuresGuidesByModelYearAndModelCode,
  };

  const mvdfFilesToBeGeneratedByModelYear = {};
  const priceGuidesToBeGeneratedByModelYear = {};
  const vehicleTechSpecsToBeGeneratedByModelYear = {};
  const topFeaturesToBeGeneratedByModelYear = {};

  const featuresGuidesToBeGeneratedByModelYear = {};

  const updatePPLFilesByModelYear = ({ pplFile, modelYearKey }) => {
    if (!updatedPPLFilesByModelYear[modelYearKey]) {
      updatedPPLFilesByModelYear[modelYearKey] = [];
    }
    const isDuplicate = Boolean(
      updatedPPLFilesByModelYear[modelYearKey].find(
        (file) => file.Key === pplFile.Key,
      ),
    );
    if (!isDuplicate) {
      updatedPPLFilesByModelYear[modelYearKey].push(pplFile);
    }
  };

  // if any of the PPL file was updated, then the related MVDF file can be updated
  Object.keys(pplFilesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(pplFilesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const pplFile = pplFilesByModelYearAndModelCode[modelYear][modelCode];
        const mvdfFile =
          mvdfFilesByModelYearAndModelCode?.[modelYear]?.[modelCode];
        const priceGuide =
          priceGuidesByModelYearAndModelCode?.[modelYear]?.[modelCode];
        const vehicleTechSpec =
          vehicleTechSpecsByModelYearAndModelCode?.[modelYear]?.[modelCode];
        const topFeatures =
          topFeaturesByModelYearAndModelCode?.[modelYear]?.[modelCode];
        const featuresGuide =
          featuresGuidesByModelYearAndModelCode?.[modelYear]?.[modelCode];

        // has related MVDF
        if (mvdfFile) {
          const currentPplVersion = pplFile.FileVersion;
          const currentPriceMatrixVersion =
            priceMatrixFilesByModelYear[modelYear]?.FileVersion;
          const currentNomenclatureGuideVersion =
            nomenclatureGuideFilesByModelYear[modelYear]?.FileVersion;
          const currentTechSpecsVersion =
            techSpecsFilesByModelYear[modelYear]?.FileVersion;

          const pplVersionUsedToGenerateMvdf =
            mvdfFile?.EN?.Metadata?.pplversion;
          const priceMatrixVersionUsedToGenerateMvdf =
            mvdfFile?.EN?.Metadata?.pricematrixversion;
          const nomenclatureGuideVersionUsedToGenerateMvdf =
            mvdfFile?.EN?.Metadata?.nomenclatureguideversion;
          const techSpecsVersionUsedToGenerateMvdf =
            mvdfFile?.EN?.Metadata?.techspecsversion;
          // if source versions doesn't match the ones that were used to generate the file
          if (
            currentPplVersion !== pplVersionUsedToGenerateMvdf ||
            currentPriceMatrixVersion !==
              priceMatrixVersionUsedToGenerateMvdf ||
            currentNomenclatureGuideVersion !==
              nomenclatureGuideVersionUsedToGenerateMvdf ||
            currentTechSpecsVersion !== techSpecsVersionUsedToGenerateMvdf
          ) {
            updatedMvdfFilesByModelYearAndModelCode = {
              ...updatedMvdfFilesByModelYearAndModelCode,
              [modelYear]: {
                ...updatedMvdfFilesByModelYearAndModelCode[modelYear],
                [modelCode]: {
                  ...updatedMvdfFilesByModelYearAndModelCode[modelYear][
                    modelCode
                  ],
                  EN: {
                    ...updatedMvdfFilesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].EN,
                    shouldUpdate: true,
                  },
                  FR: {
                    ...updatedMvdfFilesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].FR,
                    shouldUpdate: true,
                  },
                },
              },
            };
            updatePPLFilesByModelYear({ pplFile, modelYearKey: modelYear });
          }
        }
        // has related Price Guide
        if (priceGuide) {
          const currentPplVersion = pplFile.FileVersion;
          const currentPriceMatrixVersion =
            priceMatrixFilesByModelYear[modelYear]?.FileVersion;
          const currentNomenclatureGuideVersion =
            nomenclatureGuideFilesByModelYear[modelYear]?.FileVersion;
          const pplVersionUsedToGeneratePriceGuide =
            priceGuide?.dealerMsrp?.EN?.Metadata?.pplversion;
          const priceMatrixVersionUsedToGeneratePriceGuide =
            priceGuide?.dealerMsrp?.EN?.Metadata?.pricematrixversion;
          const nomenclatureGuideVersionUsedToGeneratePriceGuide =
            priceGuide?.dealerMsrp?.EN?.Metadata?.nomenclatureguideversion;

          // if source versions doesn't match the ones that were used to generate the file
          if (
            currentPplVersion !== pplVersionUsedToGeneratePriceGuide ||
            currentPriceMatrixVersion !==
              priceMatrixVersionUsedToGeneratePriceGuide ||
            currentNomenclatureGuideVersion !==
              nomenclatureGuideVersionUsedToGeneratePriceGuide
          ) {
            updatedPriceGuidesByModelYearAndModelCode = {
              ...updatedPriceGuidesByModelYearAndModelCode,
              [modelYear]: {
                ...updatedPriceGuidesByModelYearAndModelCode[modelYear],
                [modelCode]: {
                  ...updatedPriceGuidesByModelYearAndModelCode[modelYear][
                    modelCode
                  ],
                  shouldUpdate: true,
                },
              },
            };
            updatePPLFilesByModelYear({ pplFile, modelYearKey: modelYear });
          }
        }
        // has related Vehicle Tech Spec
        if (vehicleTechSpec) {
          const currentPplVersion = pplFile.FileVersion;
          const currentTechSpecsVersion =
            techSpecsFilesByModelYear[modelYear]?.FileVersion;

          const pplVersionUsedToGenerateMvdf =
            vehicleTechSpec?.EN?.Metadata?.pplversion;
          const techSpecsVersionUsedToGenerateMvdf =
            vehicleTechSpec?.EN?.Metadata?.techspecversion;
          // if source versions doesn't match the ones that were used to generate the file
          if (
            currentPplVersion !== pplVersionUsedToGenerateMvdf ||
            currentTechSpecsVersion !== techSpecsVersionUsedToGenerateMvdf
          ) {
            updatedVehicleTechSpecsByModelYearAndModelCode = {
              ...updatedVehicleTechSpecsByModelYearAndModelCode,
              [modelYear]: {
                ...updatedVehicleTechSpecsByModelYearAndModelCode[modelYear],
                [modelCode]: {
                  ...updatedVehicleTechSpecsByModelYearAndModelCode[modelYear][
                    modelCode
                  ],
                  EN: {
                    ...updatedVehicleTechSpecsByModelYearAndModelCode[
                      modelYear
                    ][modelCode].EN,
                    shouldUpdate: true,
                  },
                  FR: {
                    ...updatedVehicleTechSpecsByModelYearAndModelCode[
                      modelYear
                    ][modelCode].FR,
                    shouldUpdate: true,
                  },
                },
              },
            };
            updatePPLFilesByModelYear({ pplFile, modelYearKey: modelYear });
          }
        }
        // has related features guide
        if (featuresGuide) {
          const currentPplVersion = pplFile.FileVersion;
          const currentNomenclatureGuideVersion =
            nomenclatureGuideFilesByModelYear[modelYear]?.FileVersion;

          const pplVersionUsedToGenerateMvdf =
            featuresGuide?.EN?.Metadata?.pplversion;
          const nomenclatureGuideVersionUsedToGeneratePriceGuide =
            featuresGuide?.EN?.Metadata?.nomenclatureguideversion;
          // if source versions doesn't match the ones that were used to generate the file
          if (
            currentPplVersion !== pplVersionUsedToGenerateMvdf ||
            currentNomenclatureGuideVersion !==
              nomenclatureGuideVersionUsedToGeneratePriceGuide
          ) {
            updatedFeaturesGuidesByModelYearAndModelCode = {
              ...updatedFeaturesGuidesByModelYearAndModelCode,
              [modelYear]: {
                ...updatedFeaturesGuidesByModelYearAndModelCode[modelYear],
                [modelCode]: {
                  ...updatedFeaturesGuidesByModelYearAndModelCode[modelYear][
                    modelCode
                  ],
                  EN: {
                    ...updatedFeaturesGuidesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].EN,
                    shouldUpdate: true,
                  },
                  FR: {
                    ...updatedFeaturesGuidesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].FR,
                    shouldUpdate: true,
                  },
                },
              },
            };
            updatePPLFilesByModelYear({ pplFile, modelYearKey: modelYear });
          }
        }
        // has related top features
        if (topFeatures) {
          const currentPplVersion = pplFile.FileVersion;
          const currentNomenclatureGuideVersion =
            nomenclatureGuideFilesByModelYear[modelYear]?.FileVersion;

          const pplVersionUsedToGenerateTopFeatures =
            topFeatures?.topFive?.Metadata?.pplversion;
          const nomenclatureGuideVersionUsedToGenerateTopFeatures =
            topFeatures?.topFive?.Metadata?.nomenclatureguideversion;

          // if source versions doesn't match the ones that were used to generate the file
          if (
            currentPplVersion !== pplVersionUsedToGenerateTopFeatures ||
            currentNomenclatureGuideVersion !==
              nomenclatureGuideVersionUsedToGenerateTopFeatures
          ) {
            updatedTopFeaturesByModelYearAndModelCode = {
              ...updatedTopFeaturesByModelYearAndModelCode,
              [modelYear]: {
                ...updatedTopFeaturesByModelYearAndModelCode[modelYear],
                [modelCode]: {
                  ...updatedTopFeaturesByModelYearAndModelCode[modelYear][
                    modelCode
                  ],
                  topFive: {
                    ...updatedTopFeaturesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].topFive,
                    shouldUpdate: true,
                  },
                  topTen: {
                    ...updatedTopFeaturesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].topTen,
                    shouldUpdate: true,
                  },
                },
              },
            };
            updatePPLFilesByModelYear({ pplFile, modelYearKey: modelYear });
          }
        }

        if (
          !mvdfFile ||
          !priceGuide ||
          !vehicleTechSpec ||
          !topFeatures ||
          !featuresGuide
        ) {
          updatePPLFilesByModelYear({ pplFile, modelYearKey: modelYear });

          if (!mvdfFile) {
            if (!mvdfFilesToBeGeneratedByModelYear[modelYear]) {
              mvdfFilesToBeGeneratedByModelYear[modelYear] = [];
            }
            mvdfFilesToBeGeneratedByModelYear[modelYear].push(pplFile);
          }

          if (!priceGuide) {
            if (!priceGuidesToBeGeneratedByModelYear[modelYear]) {
              priceGuidesToBeGeneratedByModelYear[modelYear] = [];
            }
            priceGuidesToBeGeneratedByModelYear[modelYear].push(pplFile);
          }

          if (!vehicleTechSpec) {
            if (!vehicleTechSpecsToBeGeneratedByModelYear[modelYear]) {
              vehicleTechSpecsToBeGeneratedByModelYear[modelYear] = [];
            }
            vehicleTechSpecsToBeGeneratedByModelYear[modelYear].push(pplFile);
          }

          if (!topFeatures) {
            if (!topFeaturesToBeGeneratedByModelYear[modelYear]) {
              topFeaturesToBeGeneratedByModelYear[modelYear] = [];
            }
            topFeaturesToBeGeneratedByModelYear[modelYear].push(pplFile);
          }

          if (!featuresGuide) {
            if (!featuresGuidesToBeGeneratedByModelYear[modelYear]) {
              featuresGuidesToBeGeneratedByModelYear[modelYear] = [];
            }
            featuresGuidesToBeGeneratedByModelYear[modelYear].push(pplFile);
          }
        }
      },
    );
  });

  return {
    updatedPPLFilesByModelYear,
    updatedMvdfFilesByModelYearAndModelCode,
    updatedPriceGuidesByModelYearAndModelCode,
    updatedVehicleTechSpecsByModelYearAndModelCode,
    updatedTopFeaturesByModelYearAndModelCode,
    updatedFeaturesGuidesByModelYearAndModelCode,
    mvdfFilesToBeGeneratedByModelYear,
    priceGuidesToBeGeneratedByModelYear,
    vehicleTechSpecsToBeGeneratedByModelYear,
    topFeaturesToBeGeneratedByModelYear,
    featuresGuidesToBeGeneratedByModelYear,
  };
};

const checkAndGetGenerateAndUpdateStates = ({
  priceMatrixFilesByModelYear,
  pplFilesByModelYearAndModelCode,
  nomenclatureGuideFilesByModelYear,
  techSpecsFilesByModelYear,
  mvdfFilesByModelYearAndModelCode,
  priceGuidesByModelYearAndModelCode,
  vehicleTechSpecsByModelYearAndModelCode,
  topFeaturesByModelYearAndModelCode,
  featuresGuidesByModelYearAndModelCode,
  publishOverviewFiles,
}) => {
  let generateFilesSectionDescription = GENERATE_SECTION_DESCRIPTION;
  let updateAndGenerateStates = {};

  const haveAllSourceFiles = Boolean(
    hasKeys(priceMatrixFilesByModelYear) &&
      hasKeys(pplFilesByModelYearAndModelCode) &&
      hasKeys(nomenclatureGuideFilesByModelYear) &&
      hasKeys(techSpecsFilesByModelYear),
  );

  if (haveAllSourceFiles) {
    generateFilesSectionDescription = `Click below to generate files. PPL, Price Matrix, Nomenclature guides and Technical Specifications will be used to generate output files.`;
    updateAndGenerateStates = getGenerateAndUpdateState({
      priceMatrixFilesByModelYear,
      pplFilesByModelYearAndModelCode,
      nomenclatureGuideFilesByModelYear,
      techSpecsFilesByModelYear,
      mvdfFilesByModelYearAndModelCode,
      priceGuidesByModelYearAndModelCode,
      vehicleTechSpecsByModelYearAndModelCode,
      topFeaturesByModelYearAndModelCode,
      featuresGuidesByModelYearAndModelCode,
    });
  }

  const {
    updatedPPLFilesByModelYear,
    updatedMvdfFilesByModelYearAndModelCode,
    updatedPriceGuidesByModelYearAndModelCode,
    updatedVehicleTechSpecsByModelYearAndModelCode,
    updatedTopFeaturesByModelYearAndModelCode,
    updatedFeaturesGuidesByModelYearAndModelCode,
    mvdfFilesToBeGeneratedByModelYear,
    priceGuidesToBeGeneratedByModelYear,
    vehicleTechSpecsToBeGeneratedByModelYear,
    topFeaturesToBeGeneratedByModelYear,
    featuresGuidesToBeGeneratedByModelYear,
  } = updateAndGenerateStates || {};

  const out = {
    haveAllSourceFiles,
    generateFilesSectionDescription,
    formattedPplFilesByModelYear: {},
    updatedPPLFilesByModelYear: updatedPPLFilesByModelYear || {},
    updatedMvdfFilesByModelYearAndModelCode:
      updatedMvdfFilesByModelYearAndModelCode ||
      mvdfFilesByModelYearAndModelCode,
    formattedMvdfsByModelYear: {},
    updatedPriceGuidesByModelYearAndModelCode:
      updatedPriceGuidesByModelYearAndModelCode ||
      priceGuidesByModelYearAndModelCode,
    formattedPriceGuidesByModelYear: {},
    updatedVehicleTechSpecsByModelYearAndModelCode:
      updatedVehicleTechSpecsByModelYearAndModelCode ||
      vehicleTechSpecsByModelYearAndModelCode,
    formattedVehicleTechSpecsByModelYear: {},
    updatedTopFeaturesByModelYearAndModelCode:
      updatedTopFeaturesByModelYearAndModelCode ||
      topFeaturesByModelYearAndModelCode,
    formattedTopFeaturesByModelYear: {},
    updatedFeaturesGuidesByModelYearAndModelCode:
      updatedFeaturesGuidesByModelYearAndModelCode ||
      featuresGuidesByModelYearAndModelCode,
    formattedFeaturesGuidesByModelYear: {},
    mvdfFilesToBeGeneratedByModelYear: mvdfFilesToBeGeneratedByModelYear || {},
    priceGuidesToBeGeneratedByModelYear:
      priceGuidesToBeGeneratedByModelYear || {},
    vehicleTechSpecsToBeGeneratedByModelYear:
      vehicleTechSpecsToBeGeneratedByModelYear || {},
    topFeaturesToBeGeneratedByModelYear:
      topFeaturesToBeGeneratedByModelYear || {},
    featuresGuidesToBeGeneratedByModelYear:
      featuresGuidesToBeGeneratedByModelYear || {},
    updatedPublishOverviewFiles: [],
  };

  const {
    pplFilesByModelYear,
    mvdfsByModelYear,
    priceGuidesByModelYear,
    vehicleTechSpecsByModelYear,
    topFeaturesByModelYear,
    featuresGuidesByModelYear,
    updatedPublishOverviewFiles,
  } = formatProductPlanningFiles({
    pplFilesByModelYearAndModelCode,
    mvdfFilesByModelYearAndModelCode:
      out.updatedMvdfFilesByModelYearAndModelCode,
    priceGuidesByModelYearAndModelCode:
      out.updatedPriceGuidesByModelYearAndModelCode,
    vehicleTechSpecsByModelYearAndModelCode:
      out.updatedVehicleTechSpecsByModelYearAndModelCode,
    topFeaturesByModelYearAndModelCode:
      out.updatedTopFeaturesByModelYearAndModelCode,
    featuresGuidesByModelYearAndModelCode:
      out.updatedFeaturesGuidesByModelYearAndModelCode,
    publishOverviewFiles,
  });

  out.formattedPplFilesByModelYear = pplFilesByModelYear;
  out.formattedMvdfsByModelYear = mvdfsByModelYear;
  out.formattedPriceGuidesByModelYear = priceGuidesByModelYear;
  out.formattedVehicleTechSpecsByModelYear = vehicleTechSpecsByModelYear;
  out.formattedTopFeaturesByModelYear = topFeaturesByModelYear;
  out.formattedFeaturesGuidesByModelYear = featuresGuidesByModelYear;
  out.updatedPublishOverviewFiles = updatedPublishOverviewFiles || [];

  return out;
};
const productPlanningSlice = createSlice({
  name: 'productPlanning',
  initialState,
  reducers: {
    setSelectedModelYear: (state, action) => {
      state.selectedModelYear = action.payload;
    },
    setIsSourceFilesSectionCollapsed: (state, action) => {
      state.isSourceFilesSectionCollapsed = action.payload;
    },
    toggleCollapseSourceFilesSection: (state) => {
      state.isSourceFilesSectionCollapsed =
        !state.isSourceFilesSectionCollapsed;
    },
    setPPLFiles: (state, action) => {
      const latestPplFilesByModelYearAndModelCode = action.payload;
      state.pplFilesByModelYearAndModelCode =
        latestPplFilesByModelYearAndModelCode;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: latestPplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setPriceMatrixFiles: (state, action) => {
      const latestPriceMatrixFilesByModelYear = action.payload;
      state.priceMatrixFilesByModelYear = latestPriceMatrixFilesByModelYear;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: latestPriceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setNomenclatureGuideFiles: (state, action) => {
      const latestNomenclatureGuidesByModelYear = action.payload;
      state.nomenclatureGuideFilesByModelYear =
        latestNomenclatureGuidesByModelYear;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear: latestNomenclatureGuidesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setTechSpecsFiles: (state, action) => {
      const latestTechSpecsFilesByModelYear = action.payload;
      state.techSpecsFilesByModelYear = latestTechSpecsFilesByModelYear;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: latestTechSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setMVDFFiles: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode: action.payload,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setPriceGuides: (state, action) => {
      const latestPriceGuidesByModelYearAndModelCode = action.payload;
      state.priceGuidesByModelYearAndModelCode =
        latestPriceGuidesByModelYearAndModelCode;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          latestPriceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setVehicleTechSpecs: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode: action.payload,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setTopFeatures: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode: action.payload,
        featuresGuidesByModelYearAndModelCode:
          state.featuresGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setFeaturesGuides: (state, action) => {
      const latestFeaturesGuidesByModelYearAndModelCode = action.payload;
      state.featuresGuidesByModelYearAndModelCode =
        latestFeaturesGuidesByModelYearAndModelCode;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        updatedPPLFilesByModelYear,
        updatedMvdfFilesByModelYearAndModelCode,
        formattedMvdfsByModelYear,
        updatedPriceGuidesByModelYearAndModelCode,
        formattedPriceGuidesByModelYear,
        updatedVehicleTechSpecsByModelYearAndModelCode,
        formattedVehicleTechSpecsByModelYear,
        updatedTopFeaturesByModelYearAndModelCode,
        formattedTopFeaturesByModelYear,
        updatedFeaturesGuidesByModelYearAndModelCode,
        formattedFeaturesGuidesByModelYear,
        mvdfFilesToBeGeneratedByModelYear,
        priceGuidesToBeGeneratedByModelYear,
        vehicleTechSpecsToBeGeneratedByModelYear,
        topFeaturesToBeGeneratedByModelYear,
        featuresGuidesToBeGeneratedByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        nomenclatureGuideFilesByModelYear:
          state.nomenclatureGuideFilesByModelYear,
        techSpecsFilesByModelYear: state.techSpecsFilesByModelYear,
        mvdfFilesByModelYearAndModelCode:
          state.mvdfFilesByModelYearAndModelCode,
        priceGuidesByModelYearAndModelCode:
          state.priceGuidesByModelYearAndModelCode,
        vehicleTechSpecsByModelYearAndModelCode:
          state.vehicleTechSpecsByModelYearAndModelCode,
        topFeaturesByModelYearAndModelCode:
          state.topFeaturesByModelYearAndModelCode,
        featuresGuidesByModelYearAndModelCode:
          latestFeaturesGuidesByModelYearAndModelCode,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.updatedPPLFilesByModelYear = updatedPPLFilesByModelYear;
      state.mvdfFilesToBeGeneratedByModelYear =
        mvdfFilesToBeGeneratedByModelYear;
      state.mvdfFilesByModelYearAndModelCode =
        updatedMvdfFilesByModelYearAndModelCode;
      state.mvdfsByModelYear = formattedMvdfsByModelYear;
      state.priceGuidesToBeGeneratedByModelYear =
        priceGuidesToBeGeneratedByModelYear;
      state.priceGuidesByModelYearAndModelCode =
        updatedPriceGuidesByModelYearAndModelCode;
      state.priceGuidesByModelYear = formattedPriceGuidesByModelYear;
      state.vehicleTechSpecsByModelYearAndModelCode =
        updatedVehicleTechSpecsByModelYearAndModelCode;
      state.vehicleTechSpecsByModelYear = formattedVehicleTechSpecsByModelYear;
      state.vehicleTechSpecsToBeGeneratedByModelYear =
        vehicleTechSpecsToBeGeneratedByModelYear;
      state.topFeaturesByModelYearAndModelCode =
        updatedTopFeaturesByModelYearAndModelCode;
      state.topFeaturesByModelYear = formattedTopFeaturesByModelYear;
      state.topFeaturesToBeGeneratedByModelYear =
        topFeaturesToBeGeneratedByModelYear;
      state.featuresGuidesToBeGeneratedByModelYear =
        featuresGuidesToBeGeneratedByModelYear;
      state.featuresGuidesByModelYearAndModelCode =
        updatedFeaturesGuidesByModelYearAndModelCode;
      state.featuresGuidesByModelYear = formattedFeaturesGuidesByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    openGetEffectiveDatesModal: (state, action) => {
      const { selectedFilesToGenerateOrUpdate, userFileAction } =
        action.payload;
      state.shouldOpenGetEffectiveDatesModal = true;
      state.userFileAction = userFileAction;
      state.selectedFilesToGenerateOrUpdate = {
        ...state.selectedFilesToGenerateOrUpdate,
        ...selectedFilesToGenerateOrUpdate,
      };
    },
    closeGetEffectiveDatesModal: (state) => {
      state.shouldOpenGetEffectiveDatesModal = false;
      state.userFileAction = '';
      state.selectedFilesToGenerateOrUpdate = {};
    },
    updateMvdfSelection: (state, action) => {
      const { modelYear, modelCode, isSelected } = action.payload;
      const { mvdfsByModelYear } = state;

      state.mvdfsByModelYear[modelYear] = mvdfsByModelYear[modelYear].map(
        (mvdf) => {
          if (mvdf.Metadata.modelcode === modelCode) {
            return { ...mvdf, isSelected };
          }
          return mvdf;
        },
      );
    },
    intitiatedGenerateMvdf: (state, action) => {
      state.mvdfFileGenerationErrorsByModelYear = {};

      const { modelYear, pplKey } = action.payload;

      if (state.mvdfFilesToBeGeneratedByModelYear[modelYear]) {
        state.mvdfFilesToBeGeneratedByModelYear[modelYear] =
          state.mvdfFilesToBeGeneratedByModelYear[modelYear].map((pplFile) => {
            if (pplFile.Key === pplKey) {
              return {
                ...pplFile,
                isGenerating: true,
              };
            }
            return pplFile;
          });
      }
    },
    generatedMvdf: (state, action) => {
      const { modelYear, pplKey } = action.payload;

      if (state.mvdfFilesToBeGeneratedByModelYear[modelYear]) {
        state.mvdfFilesToBeGeneratedByModelYear[modelYear] =
          state.mvdfFilesToBeGeneratedByModelYear[modelYear].map((pplFile) => {
            if (pplFile.Key === pplKey) {
              return {
                ...pplFile,
                isGenerating: false,
              };
            }
            return pplFile;
          });
      }
    },
    mvdfGenerationErrored: (state, action) => {
      const { modelYear, modelCode, title, errorMsg } = action.payload;
      const { mvdfFileGenerationErrorsByModelYear, mvdfsByModelYear } = state;

      if (!mvdfFileGenerationErrorsByModelYear[modelYear]) {
        mvdfFileGenerationErrorsByModelYear[modelYear] = [];
      }
      mvdfFileGenerationErrorsByModelYear[modelYear].push({
        title,
        errorMsg,
      });

      state.mvdfsByModelYear[modelYear] = mvdfsByModelYear[modelYear].map(
        (mvdf) => {
          if (mvdf.Metadata.modelcode === modelCode) {
            return { ...mvdf, isUpdating: false };
          }
          return mvdf;
        },
      );
    },
    mvdfUpdateInitiated: (state, action) => {
      state.mvdfFileGenerationErrorsByModelYear = {};

      const { modelYear, modelCode } = action.payload;
      const { mvdfsByModelYear } = state;

      state.mvdfsByModelYear[modelYear] = mvdfsByModelYear[modelYear].map(
        (mvdf) => {
          if (mvdf.Metadata.modelcode === modelCode) {
            return { ...mvdf, isUpdating: true };
          }
          return mvdf;
        },
      );
    },
    updatedMvdf: (state, action) => {
      const { modelYear, modelCode } = action.payload;
      const { mvdfsByModelYear } = state;

      state.mvdfsByModelYear[modelYear] = mvdfsByModelYear[modelYear].map(
        (mvdf) => {
          if (mvdf.Metadata.modelcode === modelCode) {
            return { ...mvdf, isUpdating: false, shouldUpdate: false };
          }
          return mvdf;
        },
      );
    },
    mvdfPublishInitiated: (state, action) => {
      const mvdfFile = { ...action.payload };
      const modelYear = mvdfFile.Metadata.modelyear;
      const modelCode = mvdfFile.Metadata.modelcode;
      const { mvdfsByModelYear } = state;

      state.mvdfsByModelYear[modelYear] = mvdfsByModelYear[modelYear].map(
        (mvdf) => {
          if (mvdf.Metadata.modelcode === modelCode) {
            const mvdfWithStatus = {
              ...mvdf,
              status: PUBLISH_STATUS.PROCESSING,
            };
            state.publishOverviewFiles.push(mvdfWithStatus);
            return mvdfWithStatus;
          }
          return mvdf;
        },
      );
    },
    updatePriceGuideSelection: (state, action) => {
      const { modelYear, modelCode, isSelected } = action.payload;

      const { priceGuidesByModelYear } = state;
      state.priceGuidesByModelYear[modelYear] = priceGuidesByModelYear[
        modelYear
      ].map((priceGuide) => {
        if (priceGuide.Metadata.modelcode === modelCode) {
          return { ...priceGuide, isSelected };
        }
        return priceGuide;
      });
    },
    intitiatedGeneratePriceGuide: (state, action) => {
      state.priceGuideGenerationErrorsByModelYear = {};

      const { modelYear, pplKey } = action.payload;

      if (state.priceGuidesToBeGeneratedByModelYear[modelYear]) {
        state.priceGuidesToBeGeneratedByModelYear[modelYear] =
          state.priceGuidesToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: true,
                };
              }
              return pplFile;
            },
          );
      }
    },
    generatedPriceGuide: (state, action) => {
      const { modelYear, pplKey } = action.payload;

      if (state.priceGuidesToBeGeneratedByModelYear[modelYear]) {
        state.priceGuidesToBeGeneratedByModelYear[modelYear] =
          state.priceGuidesToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: false,
                };
              }
              return pplFile;
            },
          );
      }
    },
    priceGuideGenerationErrored: (state, action) => {
      const { modelYear, modelCode, title, errorMsg } = action.payload;
      const { priceGuideGenerationErrorsByModelYear, priceGuidesByModelYear } =
        state;

      if (!priceGuideGenerationErrorsByModelYear[modelYear]) {
        priceGuideGenerationErrorsByModelYear[modelYear] = [];
      }
      priceGuideGenerationErrorsByModelYear[modelYear].push({
        title,
        errorMsg,
      });

      state.priceGuidesByModelYear[modelYear] = priceGuidesByModelYear[
        modelYear
      ].map((priceGuide) => {
        if (priceGuide.Metadata.modelcode === modelCode) {
          return { ...priceGuide, isUpdating: false };
        }
        return priceGuide;
      });
    },
    priceGuideUpdateInitiated: (state, action) => {
      state.priceGuideGenerationErrorsByModelYear = {};

      const { modelYear, modelCode } = action.payload;
      const { priceGuidesByModelYear } = state;
      state.priceGuidesByModelYear[modelYear] = priceGuidesByModelYear[
        modelYear
      ].map((priceGuide) => {
        if (priceGuide.Metadata.modelcode === modelCode) {
          return { ...priceGuide, isUpdating: true };
        }
        return priceGuide;
      });
    },
    updatedPriceGuide: (state, action) => {
      const { modelYear, modelCode } = action.payload;
      const { priceGuidesByModelYear } = state;
      state.priceGuidesByModelYear[modelYear] = priceGuidesByModelYear[
        modelYear
      ].map((priceGuide) => {
        if (priceGuide.Metadata.modelcode === modelCode) {
          return { ...priceGuide, isUpdating: false, shouldUpdate: false };
        }
        return priceGuide;
      });
    },
    updateVehicleTechSpecSelection: (state, action) => {
      const { modelYear, modelCode, isSelected } = action.payload;
      const { vehicleTechSpecsByModelYear } = state;

      state.vehicleTechSpecsByModelYear[modelYear] =
        vehicleTechSpecsByModelYear[modelYear].map((vehicleTechSpec) => {
          if (vehicleTechSpec.Metadata.modelcode === modelCode) {
            return { ...vehicleTechSpec, isSelected };
          }
          return vehicleTechSpec;
        });
    },
    intitiatedGenerateVehicleTechSpec: (state, action) => {
      state.vehicleTechSpecsGenerationErrorsByModelYear = {};

      const { modelYear, pplKey } = action.payload;

      if (state.vehicleTechSpecsToBeGeneratedByModelYear[modelYear]) {
        state.vehicleTechSpecsToBeGeneratedByModelYear[modelYear] =
          state.vehicleTechSpecsToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: true,
                };
              }
              return pplFile;
            },
          );
      }
    },
    vehicleTechSpecGenerationErrored: (state, action) => {
      const { modelYear, modelCode, title, errorMsg } = action.payload;
      const {
        vehicleTechSpecsByModelYear,
        vehicleTechSpecsGenerationErrorsByModelYear,
      } = state;

      if (!vehicleTechSpecsGenerationErrorsByModelYear[modelYear]) {
        vehicleTechSpecsGenerationErrorsByModelYear[modelYear] = [];
      }
      vehicleTechSpecsGenerationErrorsByModelYear[modelYear].push({
        title,
        errorMsg,
      });
      state.vehicleTechSpecsByModelYear[modelYear] =
        vehicleTechSpecsByModelYear[modelYear].map((vehicleTechSpec) => {
          if (vehicleTechSpec.Metadata.modelcode === modelCode) {
            return { ...vehicleTechSpec, isUpdating: false };
          }
          return vehicleTechSpec;
        });
    },
    generatedVehicleTechSpec: (state, action) => {
      const { modelYear, pplKey } = action.payload;

      if (state.vehicleTechSpecsToBeGeneratedByModelYear[modelYear]) {
        state.vehicleTechSpecsToBeGeneratedByModelYear[modelYear] =
          state.vehicleTechSpecsToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: false,
                };
              }
              return pplFile;
            },
          );
      }
    },
    vehicleTechSpecUpdateInitiated: (state, action) => {
      state.vehicleTechSpecsGenerationErrorsByModelYear = {};

      const { modelYear, modelCode } = action.payload;
      const { vehicleTechSpecsByModelYear } = state;

      state.vehicleTechSpecsByModelYear[modelYear] =
        vehicleTechSpecsByModelYear[modelYear].map((vehicleTechSpec) => {
          if (vehicleTechSpec.Metadata.modelcode === modelCode) {
            return { ...vehicleTechSpec, isUpdating: true };
          }
          return vehicleTechSpec;
        });
    },
    updatedVehicleTechSpec: (state, action) => {
      const { modelYear, modelCode } = action.payload;
      const { vehicleTechSpecsByModelYear } = state;

      state.vehicleTechSpecsByModelYear[modelYear] =
        vehicleTechSpecsByModelYear[modelYear].map((vehicleTechSpec) => {
          if (vehicleTechSpec.Metadata.modelcode === modelCode) {
            return {
              ...vehicleTechSpec,
              isUpdating: false,
              shouldUpdate: false,
            };
          }
          return vehicleTechSpec;
        });
    },
    updateTopFeaturesSelection: (state, action) => {
      const { modelYear, modelCode, isSelected } = action.payload;
      const { topFeaturesByModelYear } = state;

      state.topFeaturesByModelYear[modelYear] = topFeaturesByModelYear[
        modelYear
      ].map((topFeatures) => {
        if (topFeatures.Metadata.modelcode === modelCode) {
          return { ...topFeatures, isSelected };
        }
        return topFeatures;
      });
    },
    intitiatedGenerateTopFeatures: (state, action) => {
      state.topFeaturesGenerationErrorsByModelYear = {};

      const { modelYear, pplKey } = action.payload;

      if (state.topFeaturesToBeGeneratedByModelYear[modelYear]) {
        state.topFeaturesToBeGeneratedByModelYear[modelYear] =
          state.topFeaturesToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: true,
                };
              }
              return pplFile;
            },
          );
      }
    },
    topFeaturesGenerationErrored: (state, action) => {
      const { modelYear, modelCode, title, errorMsg } = action.payload;
      const { topFeaturesByModelYear, topFeaturesGenerationErrorsByModelYear } =
        state;

      if (!topFeaturesGenerationErrorsByModelYear[modelYear]) {
        topFeaturesGenerationErrorsByModelYear[modelYear] = [];
      }
      topFeaturesGenerationErrorsByModelYear[modelYear].push({
        title,
        errorMsg,
      });
      state.topFeaturesByModelYear[modelYear] = topFeaturesByModelYear[
        modelYear
      ].map((topFeatures) => {
        if (topFeatures.Metadata.modelcode === modelCode) {
          return { ...topFeatures, isUpdating: false };
        }
        return topFeatures;
      });
    },
    generatedTopFeatures: (state, action) => {
      const { modelYear, pplKey } = action.payload;

      if (state.topFeaturesToBeGeneratedByModelYear[modelYear]) {
        state.topFeaturesToBeGeneratedByModelYear[modelYear] =
          state.topFeaturesToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: false,
                };
              }
              return pplFile;
            },
          );
      }
    },
    topFeaturesUpdateInitiated: (state, action) => {
      state.topFeaturesGenerationErrorsByModelYear = {};

      const { modelYear, modelCode } = action.payload;
      const { topFeaturesByModelYear } = state;

      state.topFeaturesByModelYear[modelYear] = topFeaturesByModelYear[
        modelYear
      ].map((topFeatures) => {
        if (topFeatures.Metadata.modelcode === modelCode) {
          return { ...topFeatures, isUpdating: true };
        }
        return topFeatures;
      });
    },
    updatedTopFeatures: (state, action) => {
      const { modelYear, modelCode } = action.payload;
      const { topFeaturesByModelYear } = state;

      state.topFeaturesByModelYear[modelYear] = topFeaturesByModelYear[
        modelYear
      ].map((topFeatures) => {
        if (topFeatures.Metadata.modelcode === modelCode) {
          return {
            ...topFeatures,
            isUpdating: false,
            shouldUpdate: false,
          };
        }
        return topFeatures;
      });
    },
    updateFeaturesGuideSelection: (state, action) => {
      const { modelYear, modelCode, isSelected } = action.payload;

      const { featuresGuidesByModelYear } = state;
      state.featuresGuidesByModelYear[modelYear] = featuresGuidesByModelYear[
        modelYear
      ].map((featuresGuide) => {
        if (featuresGuide.Metadata.modelcode === modelCode) {
          return { ...featuresGuide, isSelected };
        }
        return featuresGuide;
      });
    },
    intitiatedGenerateFeaturesGuide: (state, action) => {
      state.featuresGuideGenerationErrorsByModelYear = {};

      const { modelYear, pplKey } = action.payload;

      if (state.featuresGuidesToBeGeneratedByModelYear[modelYear]) {
        state.featuresGuidesToBeGeneratedByModelYear[modelYear] =
          state.featuresGuidesToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: true,
                };
              }
              return pplFile;
            },
          );
      }
    },
    generatedFeaturesGuide: (state, action) => {
      const { modelYear, pplKey } = action.payload;

      if (state.featuresGuidesToBeGeneratedByModelYear[modelYear]) {
        state.featuresGuidesToBeGeneratedByModelYear[modelYear] =
          state.featuresGuidesToBeGeneratedByModelYear[modelYear].map(
            (pplFile) => {
              if (pplFile.Key === pplKey) {
                return {
                  ...pplFile,
                  isGenerating: false,
                };
              }
              return pplFile;
            },
          );
      }
    },
    featuresGuideGenerationErrored: (state, action) => {
      const { modelYear, modelCode, title, errorMsg } = action.payload;
      const {
        featuresGuideGenerationErrorsByModelYear,
        featuresGuidesByModelYear,
      } = state;

      if (!featuresGuideGenerationErrorsByModelYear[modelYear]) {
        featuresGuideGenerationErrorsByModelYear[modelYear] = [];
      }
      featuresGuideGenerationErrorsByModelYear[modelYear].push({
        title,
        errorMsg,
      });

      state.featuresGuidesByModelYear[modelYear] = featuresGuidesByModelYear[
        modelYear
      ].map((featuresGuide) => {
        if (featuresGuide.Metadata.modelcode === modelCode) {
          return { ...featuresGuide, isUpdating: false };
        }
        return featuresGuide;
      });
    },
    featuresGuideUpdateInitiated: (state, action) => {
      state.featuresGuideGenerationErrorsByModelYear = {};

      const { modelYear, modelCode } = action.payload;
      const { featuresGuidesByModelYear } = state;
      state.featuresGuidesByModelYear[modelYear] = featuresGuidesByModelYear[
        modelYear
      ].map((featuresGuide) => {
        if (featuresGuide.Metadata.modelcode === modelCode) {
          return { ...featuresGuide, isUpdating: true };
        }
        return featuresGuide;
      });
    },
    updatedFeaturesGuide: (state, action) => {
      const { modelYear, modelCode } = action.payload;
      const { featuresGuidesByModelYear } = state;
      state.featuresGuidesByModelYear[modelYear] = featuresGuidesByModelYear[
        modelYear
      ].map((featuresGuide) => {
        if (featuresGuide.Metadata.modelcode === modelCode) {
          return { ...featuresGuide, isUpdating: false, shouldUpdate: false };
        }
        return featuresGuide;
      });
    },
    updateFilePublishStatus: (state, action) => {
      const { publishLogData, file, isEN } = action.payload;

      if (publishLogData) {
        const { mvdfsByModelYear } = state;
        const { status, publishedAt, publishedBy, version } = publishLogData;
        const fileType = getFileTypeFromOutputFileS3Key(publishLogData.key);

        if (fileType === OUTPUT_FILES.MVDF) {
          const modelYear = file.Metadata.modelyear;
          const modelCode = file.Metadata.modelcode;

          state.mvdfsByModelYear[modelYear] = mvdfsByModelYear[modelYear].map(
            (mvdf) => {
              if (mvdf.Metadata.modelcode === modelCode) {
                let updatedMvdf = { ...mvdf };
                if (isEN && mvdf.EN.FileVersion === version) {
                  updatedMvdf = {
                    ...mvdf,
                    EN: {
                      ...mvdf.EN,
                      status,
                      publishedAt,
                      publishedBy,
                    },
                  };
                }
                if (!isEN && mvdf.FR.FileVersion === version) {
                  updatedMvdf = {
                    ...mvdf,
                    FR: {
                      ...mvdf.FR,
                      status,
                      publishedAt,
                      publishedBy,
                    },
                  };
                }

                const statuses = [updatedMvdf.EN.status, updatedMvdf.FR.status];
                let parentStatus = '';

                if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
                  parentStatus = PUBLISH_STATUS.PROCESSING;
                } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
                  parentStatus = PUBLISH_STATUS.REJECTED;
                } else if (
                  statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
                  statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
                ) {
                  parentStatus = PUBLISH_STATUS.PUBLISHED;
                }

                updatedMvdf.status = parentStatus;
                return updatedMvdf;
              }
              return mvdf;
            },
          );
        }

        const updatedPublishOverviewFiles = [];

        state.publishOverviewFiles.forEach((overviewFile) => {
          const tempFile = { ...overviewFile };
          const enVersion = tempFile.EN;
          const frVersion = tempFile.FR;

          if (isEN && file.EN.Key === overviewFile.EN.Key) {
            tempFile.EN.status = status;
          }
          if (!isEN && file.FR.Key === overviewFile.FR.Key) {
            tempFile.FR.status = status;
          }

          const statuses = [enVersion.status, frVersion.status];
          let parentStatus = '';

          if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
            parentStatus = PUBLISH_STATUS.PROCESSING;
          } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
            parentStatus = PUBLISH_STATUS.REJECTED;
          } else if (
            statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
            statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
          ) {
            parentStatus = PUBLISH_STATUS.PUBLISHED;
          }

          tempFile.status = parentStatus;
          updatedPublishOverviewFiles.push(tempFile);
        });

        state.publishOverviewFiles = updatedPublishOverviewFiles;
      }
    },
    setPublishOverviewFiles: (state, action) => {
      state.publishOverviewFiles = action.payload;
    },
    openConfirmPublishModal: (state, action) => {
      const { selectedFilesForPublishing, userFileAction } = action.payload;
      state.shouldOpenConfirmPublishModal = true;
      state.userFileAction = userFileAction;
      state.selectedFilesForPublishing = {
        ...state.selectedFilesForPublishing,
        ...selectedFilesForPublishing,
      };
    },
    closeConfirmPublishModal: (state) => {
      state.shouldOpenConfirmPublishModal = false;
      state.userFileAction = '';
      state.selectedFilesForPublishing = {};
    },
  },
});

export default productPlanningSlice.reducer;
export const {
  setSelectedModelYear,
  setIsSourceFilesSectionCollapsed,
  toggleCollapseSourceFilesSection,
  setPPLFiles,
  setPriceMatrixFiles,
  setNomenclatureGuideFiles,
  setTechSpecsFiles,
  openGetEffectiveDatesModal,
  closeGetEffectiveDatesModal,
  // mvdf start
  setMVDFFiles,
  updateMvdfSelection,
  intitiatedGenerateMvdf,
  generatedMvdf,
  mvdfGenerationErrored,
  mvdfUpdateInitiated,
  updatedMvdf,
  mvdfPublishInitiated,
  // mvdf end
  // price guide start
  setPriceGuides,
  updatePriceGuideSelection,
  intitiatedGeneratePriceGuide,
  generatedPriceGuide,
  priceGuideGenerationErrored,
  priceGuideUpdateInitiated,
  updatedPriceGuide,
  // price guide end
  // vehicle tech specs start
  setVehicleTechSpecs,
  updateVehicleTechSpecSelection,
  intitiatedGenerateVehicleTechSpec,
  vehicleTechSpecGenerationErrored,
  generatedVehicleTechSpec,
  vehicleTechSpecUpdateInitiated,
  updatedVehicleTechSpec,
  // vehicle tech specs start
  // top features start
  setTopFeatures,
  updateTopFeaturesSelection,
  intitiatedGenerateTopFeatures,
  topFeaturesGenerationErrored,
  generatedTopFeatures,
  topFeaturesUpdateInitiated,
  updatedTopFeatures,
  // top features end
  // features guide start
  setFeaturesGuides,
  updateFeaturesGuideSelection,
  intitiatedGenerateFeaturesGuide,
  generatedFeaturesGuide,
  featuresGuideGenerationErrored,
  featuresGuideUpdateInitiated,
  updatedFeaturesGuide,
  // features guide end
  updateFilePublishStatus,
  setPublishOverviewFiles,
  openConfirmPublishModal,
  closeConfirmPublishModal,
} = productPlanningSlice.actions;
